import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Loader2, Mail, ShieldCheck } from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import { Champ, Message } from '../composants/base'

export default function Connexion() {
  const client = useQueryClient()
  const [etape, setEtape] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [info, setInfo] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  const demanderCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setErreur(null)
    setEnCours(true)
    try {
      const reponse = await api<{ message: string }>('/api/auth/code', {
        methode: 'POST',
        corps: { email },
      })
      setInfo(reponse.message)
      setEtape('code')
    } catch (e) {
      setErreur(messageErreur(e))
    } finally {
      setEnCours(false)
    }
  }

  const verifier = async (e: React.FormEvent) => {
    e.preventDefault()
    setErreur(null)
    setEnCours(true)
    try {
      await api('/api/auth/verifier', { methode: 'POST', corps: { email, code } })
      await client.invalidateQueries({ queryKey: ['moi'] })
    } catch (e) {
      setErreur(messageErreur(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 text-center">
          <div className="text-[26px] font-semibold tracking-tight text-bleu-700">doc</div>
          <p className="mt-1 text-[13px] text-brume">Dossiers patients</p>
        </div>

        <div className="carte px-6 py-6">
          {etape === 'email' ? (
            <form onSubmit={demanderCode} className="flex flex-col gap-4">
              <Champ
                etiquette="Adresse e-mail"
                aide="Un code à six chiffres y sera envoyé. Il n'y a pas de mot de passe."
              >
                <input
                  className="champ"
                  type="email"
                  autoFocus
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom.nom@exemple.fr"
                />
              </Champ>

              {erreur && <Message ton="critique">{erreur}</Message>}

              <button className="bouton-primaire w-full" disabled={enCours}>
                {enCours ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Recevoir un code
              </button>
            </form>
          ) : (
            <form onSubmit={verifier} className="flex flex-col gap-4">
              {info && <Message ton="info">{info}</Message>}

              <Champ etiquette="Code reçu par e-mail">
                <input
                  className="champ text-center text-[22px] tracking-[0.4em]"
                  inputMode="numeric"
                  autoFocus
                  required
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                />
              </Champ>

              {erreur && <Message ton="critique">{erreur}</Message>}

              <button className="bouton-primaire w-full" disabled={enCours || code.length !== 6}>
                {enCours ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                Se connecter
              </button>

              <button
                type="button"
                className="bouton-discret w-full"
                onClick={() => {
                  setEtape('email')
                  setCode('')
                  setErreur(null)
                }}
              >
                Changer d’adresse ou redemander un code
              </button>
            </form>
          )}
        </div>

        <p className="mt-5 flex items-start gap-2 px-2 text-[12px] leading-relaxed text-brume">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          Les dossiers contiennent des données de santé. Les documents sont chiffrés sur le serveur
          et chaque accès est journalisé.
        </p>
      </div>
    </div>
  )
}
